import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const Verify2FA = () => {
  const navigate = useNavigate();
  const { verify2FA, getDashboardUrl } = useAuth();
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  useEffect(() => {
    // Check if we have 2FA pending
    const storedEmail = sessionStorage.getItem('2fa_email');
    const pending2FA = sessionStorage.getItem('2fa_pending');
    
    if (!storedEmail || pending2FA !== 'true') {
      // No 2FA pending, redirect to login
      navigate('/login');
    } else {
      setEmail(storedEmail);
    }
  }, [navigate]);

  const onSubmit = async (data) => {
    try {
      const result = await verify2FA(email, data.otp);
      
      if (result?.success) {
        // 2FA verification successful, redirect to dashboard
        const dashboardUrl = getDashboardUrl();
        navigate(dashboardUrl, { replace: true });
      }
    } catch (error) {
      // Error is handled in verify2FA function
    }
  };

  const resendOTP = async () => {
    try {
      setResending(true);
      const formData = new FormData();
      formData.append('identifier', email);
      formData.append('type', 'email');
      formData.append('flow', 'login');
      
      const response = await api.post('/resend-otp', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      if (response.data?.success || response.data?.message?.includes('sent')) {
        toast.success('OTP resent successfully!');
      } else {
        toast.error(response.data?.message || 'Failed to resend OTP');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const cancelVerification = () => {
    // Clear 2FA session data and return to login
    sessionStorage.removeItem('2fa_email');
    sessionStorage.removeItem('2fa_pending');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl p-8">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4"
            >
              <Lock className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h1>
            <p className="text-sm text-gray-600">
              Enter the 6-digit code sent to
              <br />
              <span className="font-medium text-gray-800">{email}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* OTP Field */}
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                id="otp"
                type="text"
                className={`input-field text-center text-2xl tracking-widest font-mono ${
                  errors.otp ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
                }`}
                placeholder="000000"
                maxLength="6"
                autoComplete="off"
                {...register('otp', {
                  required: 'Verification code is required',
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: 'Code must be 6 digits',
                  },
                })}
              />
              {errors.otp && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-1 text-sm text-red-600"
                >
                  {errors.otp.message}
                </motion.p>
              )}
            </div>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg 
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24"
                  >
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                    ></circle>
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Verify & Sign In'
              )}
            </motion.button>
          </form>

          {/* Resend OTP */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Didn't receive the code?{' '}
              <button
                onClick={resendOTP}
                disabled={resending}
                className="font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending ? 'Sending...' : 'Resend Code'}
              </button>
            </p>
          </div>

          {/* Cancel and Return to Login */}
          <div className="mt-4">
            <button
              onClick={cancelVerification}
              className="flex items-center justify-center space-x-2 text-sm text-gray-600 hover:text-primary-600 transition-colors mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Cancel & Return to Login</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Verify2FA;